import type { SnipcatClient } from "./client";
import { describeDevice } from "./device";
import type { SnipcatPlatform } from "./types";
import { eventKey, sanitizeValues } from "./values";

/**
 * Declarative tracking driven by data attributes, so a marketing page can be
 * instrumented in the markup instead of in a script nobody redeploys:
 *
 *   <button data-snipcat-event="cta_click" data-snipcat-variant="hero">
 *   <a data-snipcat-view="cta_hero" href="…">          (recorded once when seen)
 *   <section data-snipcat-step="pricing">            (timed while on screen)
 *   <a data-snipcat-claim="ios" href="https://apps.apple.com/…">
 *   <form data-snipcat-lead>                          (submitted as a lead)
 *     <input name="email"> <input name="name"> <input name="phone">
 *
 * Every hook is optional and additive - a page with none of these attributes
 * still gets its page view, its time on page, how far down it was scrolled and
 * a coarse description of the device (`device_*`, see device.ts).
 */

export interface AutoTrackOptions {
  /** See SnipcatConfig.scrollDepth. */
  scrollDepth?: boolean | number[];
}

const DEFAULT_SCROLL_MILESTONES = [25, 50, 75, 100];

/** A stable, bounded id for the current page. */
const pageId = (): string => {
  const path = (window.location.pathname || "/").replace(/\/+$/, "") || "/";
  return path.slice(0, 64);
};

const isVisible = (entry: IntersectionObserverEntry): boolean =>
  entry.isIntersecting && entry.intersectionRatio > 0;

/**
 * Runs `fn` once the markup exists. The hooks below query the DOM, and a
 * script tag in <head> without `defer` initialises before <body> is parsed -
 * every [data-snipcat-*] element would then simply go unnoticed, silently.
 */
const whenDomReady = (fn: () => void): void => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  } else {
    fn();
  }
};

const normalizeMilestones = (
  option: AutoTrackOptions["scrollDepth"],
): number[] => {
  if (option === false) {
    return [];
  }
  const list = Array.isArray(option) ? option : DEFAULT_SCROLL_MILESTONES;
  return Array.from(
    new Set(
      list
        .map((item) => Math.round(Number(item)))
        .filter((item) => Number.isFinite(item) && item > 0 && item <= 100),
    ),
  ).sort((a, b) => a - b);
};

/**
 * How much of the page has been on screen so far, in percent of its full
 * height. A page that fits the viewport is 100 % without a single scroll -
 * the visitor did see all of it, which is what the number is meant to say.
 */
const scrolledPercent = (): number => {
  const doc = document.documentElement;
  const total = Math.max(
    doc ? doc.scrollHeight : 0,
    document.body ? document.body.scrollHeight : 0,
  );
  if (!total) {
    return 0;
  }
  const viewport = window.innerHeight || (doc ? doc.clientHeight : 0);
  const top = window.pageYOffset || (doc ? doc.scrollTop : 0) || 0;
  return Math.min(100, Math.round(((top + viewport) / total) * 100));
};

/**
 * Scroll depth as milestones rather than a running maximum: each milestone
 * is ONE key written ONCE, so a visitor scrolling the whole page costs four
 * patches at most, while a running maximum would re-send on every scroll
 * tick. The value is the moment the milestone was first reached, so "how long
 * until they got to the pricing" is answerable as well.
 */
const attachScrollDepth = (
  client: SnipcatClient,
  milestones: number[],
): void => {
  if (!milestones.length) {
    return;
  }
  const page = pageId();
  let pending = [...milestones];
  let scheduled = false;

  const check = (): void => {
    scheduled = false;
    if (!pending.length) {
      return;
    }
    const percent = scrolledPercent();
    const reached = pending.filter((item) => percent >= item);
    if (!reached.length) {
      return;
    }
    pending = pending.filter((item) => percent < item);
    reached.forEach((item) => {
      client.trackEvent("scroll_" + item, page);
    });
    if (!pending.length) {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    }
  };
  const onScroll = (): void => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    // one measurement per frame, whatever the scroll event rate
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(check);
    } else {
      setTimeout(check, 16);
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  // The first measurement waits for `load`: before images and fonts are in,
  // the page is shorter than it will be, and a short page reads as "100 %
  // seen" for a visitor who has not seen anything yet.
  if (document.readyState === "complete") {
    onScroll();
  } else {
    window.addEventListener("load", onScroll, { once: true });
  }
};

/**
 * Impressions of marked elements - typically CTAs - recorded once per page
 * load as `view_<id>` the first time at least half of the element is on
 * screen. Together with the click event of the same element this answers the
 * question a click count alone cannot: was the CTA ignored, or never reached?
 *
 * The id is the attribute's value; an EMPTY `data-snipcat-view` on an element
 * that also carries data-snipcat-event reuses that event's key, so one CTA is
 * marked once and yields the pair `cta_click_hero` / `view_cta_click_hero`.
 */
const attachViewTracking = (client: SnipcatClient): void => {
  if (typeof IntersectionObserver !== "function") {
    return;
  }
  const elements = Array.from(
    document.querySelectorAll<HTMLElement>("[data-snipcat-view]"),
  );
  if (!elements.length) {
    return;
  }
  const viewId = (el: HTMLElement): string | null => {
    const explicit = (el.dataset.snipcatView || "").trim();
    if (explicit) {
      return explicit;
    }
    const event = (el.dataset.snipcatEvent || "").trim();
    return event ? eventKey(event, el.dataset.snipcatVariant || null) : null;
  };
  const seen = new Set<string>();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          return;
        }
        const el = entry.target as HTMLElement;
        observer.unobserve(el);
        const id = viewId(el);
        if (!id || seen.has(id)) {
          return;
        }
        seen.add(id);
        client.trackEvent("view", id);
      });
    },
    { threshold: [0.5] },
  );
  elements.forEach((el) => observer.observe(el));
};

export const attachAutoTracking = (
  client: SnipcatClient,
  options: AutoTrackOptions = {},
): void => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  // ── device + page view + time on page ─────────────────────────────────
  // Queued together, so the device rides in the page-view patch for free.
  client.track(describeDevice());
  client.trackEvent("page_view", pageId());
  client.trackStep(pageId());

  // ── scroll depth ──────────────────────────────────────────────────────
  attachScrollDepth(client, normalizeMilestones(options.scrollDepth));

  // ── impressions of marked elements (CTAs) ─────────────────────────────
  whenDomReady(() => attachViewTracking(client));

  // ── clicks ────────────────────────────────────────────────────────────
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const eventEl = target.closest<HTMLElement>("[data-snipcat-event]");
      if (eventEl) {
        client.trackEvent(
          eventEl.dataset.snipcatEvent || "click",
          eventEl.dataset.snipcatVariant || null,
        );
      }

      const claimEl = target.closest<HTMLElement>("[data-snipcat-claim]");
      if (claimEl) {
        const platform = (claimEl.dataset.snipcatClaim || "").toLowerCase();
        if (platform === "ios" || platform === "android") {
          // Fire and forget: the store link must open now, and the claim is
          // matched by ip+platform on the app's first launch anyway.
          void client.claim(platform as SnipcatPlatform);
        }
      }
    },
    // capture, so a handler that stops propagation cannot silence tracking
    true,
  );

  // ── sections timed while on screen ────────────────────────────────────
  whenDomReady(() => {
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>("[data-snipcat-step]"),
    );
    if (!sections.length || typeof IntersectionObserver !== "function") {
      return;
    }
    let active: string | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        // the section covering most of the viewport is the one being read
        const visible = entries
          .filter(isVisible)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const id = visible
          ? (visible.target as HTMLElement).dataset.snipcatStep || null
          : null;
        if (id && id !== active) {
          active = id;
          client.trackStep(id);
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75] },
    );
    sections.forEach((section) => observer.observe(section));
  });

  // ── lead forms ────────────────────────────────────────────────────────
  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (
      !(form instanceof HTMLFormElement) ||
      !form.matches("[data-snipcat-lead]")
    ) {
      return;
    }
    event.preventDefault();

    const data = new FormData(form);
    const read = (...names: string[]): string | null => {
      for (const name of names) {
        const value = data.get(name);
        if (typeof value === "string" && value.trim()) {
          return value.trim();
        }
      }
      return null;
    };

    // everything that is NOT a contact detail is funnel telemetry
    const extras: Record<string, unknown> = {};
    data.forEach((value, key) => {
      if (typeof value === "string") {
        extras[key] = value;
      }
    });
    const telemetry = sanitizeValues(extras);
    // which form on the page this was - a landing page usually has the same
    // capture in the hero and in the footer, and "which one converts" is the
    // first question anybody asks about it
    if (form.dataset.snipcatSource) {
      telemetry.lead_form = form.dataset.snipcatSource;
    }
    if (Object.keys(telemetry).length) {
      client.track(telemetry);
    }

    const submitter = form.querySelector<HTMLButtonElement | HTMLInputElement>(
      "[type=submit]",
    );
    if (submitter) {
      submitter.disabled = true;
    }

    void client
      .submitLead({
        name: read("name", "fullname", "full_name", "jmeno"),
        email: read("email", "e-mail", "mail"),
        phone: read("phone", "tel", "telefon", "mobile"),
      })
      .then((result) => {
        if (submitter) {
          submitter.disabled = false;
        }
        // The page decides what happens next; the SDK only reports. A
        // redirect target in the markup is honoured because that is the one
        // thing every plain HTML form needs and cannot express otherwise.
        const redirect = form.dataset.snipcatRedirect;
        form.dispatchEvent(
          new CustomEvent("snipcat:lead", {
            bubbles: true,
            detail: result,
          }),
        );
        if (result.ok && redirect) {
          window.location.href = redirect;
        }
      });
  });
};
